import React, { useState, useRef } from 'react';
import { excelAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  Download, Upload, FileSpreadsheet, Target,
  Loader2, CheckCircle, AlertTriangle, FileDown, Info 
} from 'lucide-react';

// 21 Oct 2026: Purchases import/export is retired along with Offers & Purchases —
// this page now only covers Targets.
export default function BulkImportExportPage() {
  const [activeTab, setActiveTab] = useState('export');
  const [loading, setLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const { hasPermission } = useAuth();

  const canImportExport = hasPermission('import_export_excel');

  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await excelAPI.exportTargets();

      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `targets_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Targets exported successfully');
    } catch (error) {
      toast.error(`Export failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setLoading(true);
    try {
      const response = await excelAPI.getTemplate('targets');
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'targets_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Please select an Excel file (.xlsx)');
        return;
      }
      setSelectedFile(file);
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setLoading(true);
    setImportResults(null);
    
    try {
      const response = await excelAPI.importTargets(selectedFile);

      setImportResults(response.data);
      
      if (response.data.created > 0) {
        toast.success(`Successfully imported ${response.data.created} records`);
      }
      if (response.data.skipped > 0) {
        toast.warning(`${response.data.skipped} rows were skipped`);
      }
    } catch (error) {
      toast.error(`Import failed: ${error.response?.data?.detail || error.message}`);
      setImportResults({ status: 'error', message: error.response?.data?.detail || error.message });
    } finally {
      setLoading(false);
    }
  };

  const clearImport = () => {
    setSelectedFile(null);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!canImportExport) {
    return (
      <div className="space-y-6" data-testid="bulk-import-export-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Bulk Import/Export</h1>
          <p className="text-muted-foreground">Manage data via Excel files</p>
        </div>
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to import/export data. Contact your administrator.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bulk-import-export-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Bulk Import/Export</h1>
        <p className="text-muted-foreground">Import and export targets via Excel</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="export" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export
          </TabsTrigger>
          <TabsTrigger value="import" className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Import
          </TabsTrigger>
        </TabsList>

        {/* Export Tab */}
        <TabsContent value="export" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Export Targets */}
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Export Targets
                </CardTitle>
                <CardDescription>
                  Download all price targets as Excel file
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-muted/50 rounded-sm text-sm">
                    <p className="font-medium mb-2">Includes:</p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1">
                      <li>SKU codes and item names</li>
                      <li>Target type (Range/Single)</li>
                      <li>Min, Max, Average prices</li>
                      <li>Period and expected quantities</li>
                      <li>Effective dates and notes</li>
                    </ul>
                  </div>
                  <Button 
                    onClick={handleExport} 
                    disabled={loading}
                    className="w-full rounded-sm"
                    data-testid="export-targets-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                    Export Targets to Excel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Import Tab */}
        <TabsContent value="import" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Import Form */}
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5 text-primary" />
                  Import Targets
                </CardTitle>
                <CardDescription>
                  Upload Excel file to create bulk target records
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Download Template */}
                <div className="space-y-2">
                  <Label>Step 1: Get the template</Label>
                  <Button
                    variant="outline"
                    onClick={handleDownloadTemplate}
                    disabled={loading}
                    className="w-full rounded-sm"
                    data-testid="download-template-btn"
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    Download Targets Template
                  </Button>
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label>Step 2: Upload your file</Label>
                  <div className="flex gap-2">
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      className="rounded-sm"
                      data-testid="file-input"
                    />
                    {selectedFile && (
                      <Button variant="ghost" size="sm" onClick={clearImport}>
                        Clear
                      </Button>
                    )}
                  </div>
                  {selectedFile && (
                    <p className="text-sm text-muted-foreground">
                      Selected: <span className="font-medium">{selectedFile.name}</span> ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>

                {/* Import Button */}
                <Button
                  onClick={handleImport}
                  disabled={loading || !selectedFile}
                  className="w-full rounded-sm"
                  data-testid="import-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Import Targets
                </Button>
              </CardContent>
            </Card>

            {/* Import Results / Instructions */}
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {importResults ? (
                    importResults.status === 'error' ? (
                      <AlertTriangle className="h-5 w-5 text-destructive" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                    )
                  ) : (
                    <Info className="h-5 w-5 text-blue-500" />
                  )}
                  {importResults ? 'Import Results' : 'Instructions'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {importResults ? (
                  <div className="space-y-4">
                    {importResults.status === 'error' ? (
                      <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{importResults.message}</AlertDescription>
                      </Alert>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-sm text-center">
                            <div className="text-2xl font-bold text-emerald-600">{importResults.created}</div>
                            <div className="text-sm text-emerald-700">Created</div>
                          </div>
                          <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm text-center">
                            <div className="text-2xl font-bold text-amber-600">{importResults.skipped}</div>
                            <div className="text-sm text-amber-700">Skipped</div>
                          </div>
                        </div>
                        
                        {importResults.errors && importResults.errors.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-destructive">Errors:</p>
                            <div className="max-h-[200px] overflow-auto space-y-1">
                              {importResults.errors.map((err, idx) => (
                                <div key={idx} className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                                  {err}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    
                    <Button variant="outline" onClick={clearImport} className="w-full rounded-sm">
                      Import Another File
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4 text-sm text-muted-foreground">
                    <div>
                      <p className="font-medium text-foreground mb-2">For Targets:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>SKU must exist in your Zoho Items</li>
                        <li>Target Type: RANGE or SINGLE</li>
                        <li>For RANGE: provide Min and Max</li>
                        <li>For SINGLE: provide Avg price</li>
                        <li>Date format: YYYY-MM-DD</li>
                      </ul>
                    </div>
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        Download the template first to see the exact format required.
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

